import React, { useMemo } from 'react';
import { Grid, Stack } from '@mui/material';
import UIContainer from 'components/ui/UIContainer';
import { useAppSelector } from 'app/hooks';
import { VocabularyTypeRecord } from 'app/types';
import { MOCK_STATS } from './mockStats';
import TodayGoalCard from './components/TodayGoalCard';
import StreakCard from './components/StreakCard';
import WeeklySnapshot from './components/WeeklySnapshot';
import GoalsCard from './components/GoalsCard';
import AchievementsCard from './components/AchievementsCard';
import ActivityHeatmap from './components/ActivityHeatmap';
import RecommendationStrip from './components/RecommendationStrip';

const sumVocabulary = (
  data: VocabularyTypeRecord[],
  key: keyof Pick<
    VocabularyTypeRecord,
    'inActiveSpeech' | 'learned' | 'needToLearn' | 'totalCount'
  >,
) => data.reduce((acc, record) => acc + Number(record[key] || 0), 0);

const HomePage: React.FC = () => {
  const vocabulary = useAppSelector((store) => store.common.vocabulary.data);

  // Not yet consumed — kept ready to feed real vocabulary totals into the
  // composition card once a stats endpoint replaces MOCK_STATS (see below).
  const _totals = useMemo(
    () => ({
      activeSpeech: sumVocabulary(vocabulary, 'inActiveSpeech'),
      learned: sumVocabulary(vocabulary, 'learned'),
      needToLearn: sumVocabulary(vocabulary, 'needToLearn'),
      total: sumVocabulary(vocabulary, 'totalCount'),
    }),
    [vocabulary],
  );

  // While we don't have a real stats endpoint, the dashboard runs on MOCK_STATS.
  // Composition still uses real `vocabulary` totals when present so the bottom
  // card stays truthful; the rest is illustrative.
  const stats = MOCK_STATS;

  return (
    <UIContainer sx={{ mb: 8, mt: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <TodayGoalCard goal={stats.todayGoal} />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <StreakCard
              current={stats.streak.current}
              longest={stats.streak.longest}
              activeToday={stats.streak.activeToday}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <GoalsCard goals={stats.goals} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <AchievementsCard
              next={stats.nextAchievement}
              recent={stats.recentAchievements}
            />
          </Grid>
        </Grid>

        <WeeklySnapshot metrics={stats.weekly} />

        <ActivityHeatmap
          byYear={stats.heatmapByYear}
          years={stats.availableYears}
        />

        <RecommendationStrip items={stats.recommendations} />
      </Stack>
    </UIContainer>
  );
};

export default HomePage;
